import { useContext, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
    FiAlertCircle,
    FiCheckCircle,
    FiClock,
    FiDollarSign,
    FiList,
    FiTrendingDown,
    FiTrendingUp,
    FiXCircle,
} from "react-icons/fi";
import { OrdersState } from "../../Redux/OrdersContext";
import { UserContext } from "../../Redux/UserContext";
import "./MonthlyInsights.css";

// Branch IDs 1-8 match the DB PKs (BigAutoField, starts at 1).
// ID 0 is the synthetic "urgent" category in the slicer — no order has pickup_branch=0.
const BRANCH_NAMES: Record<number, string> = {
    1: "ביג",
    2: "דיסק קלאב",
    3: "אייסמול",
    4: "רד",
    5: "טיילת",
    6: "משלוח לבית הלקוח באילת",
    7: "משלוח לבית הלקוח",
    8: "מחסן ראשי",
};

const MONTH_NAMES = [
    "ינואר", "פברואר", "מרץ", "אפריל", "מאי", "יוני",
    "יולי", "אוגוסט", "ספטמבר", "אוקטובר", "נובמבר", "דצמבר",
];

function MonthlyInsights(): JSX.Element {
    const context = useContext(UserContext);
    const navigate = useNavigate();
    const allOrders = useSelector((state: OrdersState) => state.orders.value);

    const today = new Date();
    const [selectedMonth, setSelectedMonth] = useState(today.getMonth()); // 0-indexed
    const [selectedYear, setSelectedYear] = useState(today.getFullYear());

    useEffect(() => {
        if (context.user === null) {
            navigate("/login");
        }
    }, [context.user, navigate]);

    // Filter orders for the selected month/year using order_date (creation date).
    // order_date is a date-only string ("YYYY-MM-DD") which JS parses as UTC midnight,
    // so we use getUTC* to avoid local-timezone month mismatches.
    const monthOrders = allOrders.filter((order) => {
        const d = new Date(order.order_date);
        return d.getUTCMonth() === selectedMonth && d.getUTCFullYear() === selectedYear;
    });

    // Core stats
    const total = monthOrders.length;
    const paid = monthOrders.filter((o) => o.is_paid);
    const cancelled = monthOrders.filter((o) => o.is_cancelled);
    const active = monthOrders.filter((o) => !o.is_paid && !o.is_cancelled);
    const readyNotPaid = monthOrders.filter(
        (o) => o.is_ready_for_pickup && !o.is_paid && !o.is_cancelled
    );

    // Revenue — DRF serialises DecimalField as a string; Number() coerces safely,
    // and || 0 guards against null/empty values that would produce NaN.
    const revenue = paid.reduce(
        (sum, o) => sum + (Number(o.total_price) || 0),
        0
    );
    const cancelledValue = cancelled.reduce(
        (sum, o) => sum + (Number(o.total_price) || 0),
        0
    );
    // Total business volume: all non-cancelled orders (paid + active)
    const nonCancelledValue = revenue + monthOrders
        .filter((o) => !o.is_paid && !o.is_cancelled)
        .reduce((sum, o) => sum + (Number(o.total_price) || 0), 0);
    const totalValue = monthOrders.reduce(
        (sum, o) => sum + (Number(o.total_price) || 0),
        0
    );
    const avgOrderValue = total > 0 ? totalValue / total : 0;

    // Branch breakdown
    const branchCounts: Record<number, number> = {};
    monthOrders.forEach((o) => {
        branchCounts[o.pickup_branch] = (branchCounts[o.pickup_branch] || 0) + 1;
    });
    const maxBranchCount = Math.max(...Object.values(branchCounts), 1);

    // Available years derived from order data + current year.
    // Use getUTCFullYear() to match the same UTC interpretation used in the filter.
    const availableYears = Array.from(
        new Set([
            today.getFullYear(),
            ...allOrders.map((o) => new Date(o.order_date).getUTCFullYear()),
        ])
    ).sort((a, b) => b - a);

    const pct = (n: number) =>
        total > 0 ? Math.round((n / total) * 100) : 0;

    return (
        <div className="MonthlyInsights">
            {/* ── Header ── */}
            <div className="insights-header">
                <h1>סיכום חודשי</h1>
                <div className="month-selector">
                    <select
                        value={selectedMonth}
                        onChange={(e) => setSelectedMonth(Number(e.target.value))}
                    >
                        {MONTH_NAMES.map((name, i) => (
                            <option key={i} value={i}>
                                {name}
                            </option>
                        ))}
                    </select>
                    <span className="selector-divider">|</span>
                    <select
                        value={selectedYear}
                        onChange={(e) => setSelectedYear(Number(e.target.value))}
                    >
                        {availableYears.map((y) => (
                            <option key={y} value={y}>
                                {y}
                            </option>
                        ))}
                    </select>
                    <span className="month-label">
                        {MONTH_NAMES[selectedMonth]} {selectedYear}
                    </span>
                </div>
            </div>

            {/* ── Content ── */}
            <div className="insights-content">
                {total === 0 ? (
                    <p className="no-data">לא נמצאו הזמנות לחודש זה</p>
                ) : (
                    <>
                        {/* ── Stat Cards ── */}
                        <div className="stats-grid">
                            <div className="stat-card total">
                                <div className="stat-icon icon-total">
                                    <FiList size={22} />
                                </div>
                                <div className="stat-number">{total}</div>
                                <div className="stat-label">סה"כ הזמנות</div>
                            </div>

                            <div className="stat-card paid">
                                <div className="stat-icon icon-paid">
                                    <FiCheckCircle size={22} />
                                </div>
                                <div className="stat-number">{paid.length}</div>
                                <div className="stat-label">שולמו</div>
                                <div className="stat-footer">
                                    <span className="stat-pct">{pct(paid.length)}%</span>
                                    <div className="progress-bar">
                                        <div
                                            className="progress-fill paid-fill"
                                            style={{ width: `${pct(paid.length)}%` }}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="stat-card cancelled">
                                <div className="stat-icon icon-cancelled">
                                    <FiXCircle size={22} />
                                </div>
                                <div className="stat-number">{cancelled.length}</div>
                                <div className="stat-label">בוטלו</div>
                                <div className="stat-footer">
                                    <span className="stat-pct">{pct(cancelled.length)}%</span>
                                    <div className="progress-bar">
                                        <div
                                            className="progress-fill cancelled-fill"
                                            style={{ width: `${pct(cancelled.length)}%` }}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="stat-card active">
                                <div className="stat-icon icon-active">
                                    <FiClock size={22} />
                                </div>
                                <div className="stat-number">{active.length}</div>
                                <div className="stat-label">פעילות</div>
                                <div className="stat-footer">
                                    <span className="stat-pct">{pct(active.length)}%</span>
                                    <div className="progress-bar">
                                        <div
                                            className="progress-fill active-fill"
                                            style={{ width: `${pct(active.length)}%` }}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="stat-card revenue">
                                <div className="stat-icon icon-revenue">
                                    <FiDollarSign size={22} />
                                </div>
                                <div className="stat-number stat-number--sm">
                                    ₪{nonCancelledValue.toLocaleString("he-IL", { maximumFractionDigits: 0 })}
                                </div>
                                <div className="stat-label">סה"כ ערך הזמנות</div>
                            </div>

                            <div className="stat-card cancelled-value">
                                <div className="stat-icon icon-cancelled">
                                    <FiTrendingDown size={22} />
                                </div>
                                <div className="stat-number stat-number--sm">
                                    ₪{cancelledValue.toLocaleString("he-IL", { maximumFractionDigits: 0 })}
                                </div>
                                <div className="stat-label">ערך הזמנות שבוטלו</div>
                            </div>

                            <div className="stat-card avg">
                                <div className="stat-icon icon-avg">
                                    <FiTrendingUp size={22} />
                                </div>
                                <div className="stat-number stat-number--sm">
                                    ₪{avgOrderValue.toLocaleString("he-IL", { maximumFractionDigits: 0 })}
                                </div>
                                <div className="stat-label">ממוצע הזמנה</div>
                            </div>

                            {readyNotPaid.length > 0 && (
                                <div className="stat-card ready-not-paid">
                                    <div className="stat-icon icon-alert">
                                        <FiAlertCircle size={22} />
                                    </div>
                                    <div className="stat-number">{readyNotPaid.length}</div>
                                    <div className="stat-label">מוכן - לא שולם</div>
                                </div>
                            )}
                        </div>

                        {/* ── Branch Breakdown ── */}
                        {Object.keys(branchCounts).length > 0 && (
                            <div className="section">
                                <p className="section-title">הזמנות לפי סניף</p>
                                <div className="branch-bars">
                                    {Object.entries(branchCounts)
                                        .sort((a, b) => b[1] - a[1])
                                        .map(([branchId, count]) => (
                                            <div className="branch-row" key={branchId}>
                                                <span className="branch-name">
                                                    {BRANCH_NAMES[Number(branchId)] || `סניף ${branchId}`}
                                                </span>
                                                <div className="branch-bar-track">
                                                    <div
                                                        className="branch-bar-fill"
                                                        style={{ width: `${(count / maxBranchCount) * 100}%` }}
                                                    />
                                                </div>
                                                <span className="branch-count">{count}</span>
                                            </div>
                                        ))}
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}

export default MonthlyInsights;
